// CIM data room — the dealmaker's side of the owner request list
// (routes/data_room.py, app/data_room.py). It lives in the vault manager as
// the first rail entry (and as the CIM Command Center's Data room tab): the
// list of everything we need from the owners, grouped by the vault folder
// each item files into, with who has access, the threads with the owners
// and the review loop (accept / needs changes / not applicable).
//
// Drafts (an import, the MCP tool, an architecture run, "Save as draft")
// reach the owners only when someone ticks and sends them from To approve —
// explicit ids, never "all". Dismissed drafts stay out until restored.
//
// The owners' side is the portal (atlas-crm/portal/, src/portal/entry.jsx);
// what they upload lands in the vault and shows up on the item here.
//
// window.DataRoomPane({ companyId, v, phone, q, initialFilter }) — `v` is the
// vault state (useVault) so an item can link a file that's already in the
// vault; `initialFilter` 'approve' opens To approve (default 'all').

const DR_STATUS_HUE = { none: 'slate', prog: 'blue', up: 'teal', rev: 'amber', fix: 'rose', ok: 'green', na: 'slate' };
const DR_PRIORITY = { P0: 'Essential', P1: 'High value', P2: 'Supplemental' };
const DR_ROOM_STATUS = { draft: 'Draft', open: 'Open', closed: 'Closed' };
const DR_KIND = { file: 'File', call: 'Call', question: 'Question' };
const DR_LOOKUP_HUE = { found: 'green', possible: 'amber', not_found: 'slate' };
// Sent (or internal) and still live. Drafts wait in To approve; dismissed
// drafts and archived items have their own filters.
const _drLive = (i) => !i.archived && !i.dismissed && !i.draft;
const DR_FILTERS = [
  { key: 'approve', label: 'To approve', test: (i) => !!i.draft },
  { key: 'all', label: 'All', test: _drLive },
  { key: 'review', label: 'To review', test: (i) => _drLive(i) && (i.status === 'up' || i.status === 'rev') },
  { key: 'owner', label: 'Waiting on owner', test: (i) => _drLive(i) && !i.internal && ['none', 'prog', 'fix'].includes(i.status) },
  { key: 'ok', label: 'Accepted', test: (i) => _drLive(i) && i.status === 'ok' },
  { key: 'dismissed', label: 'Dismissed', test: (i) => !!i.dismissed && !i.archived },
  { key: 'archived', label: 'Archived', test: (i) => i.archived },
];

const _drInput = {
  width: '100%', fontSize: 12.5, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box',
};
const _drLabel = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 4 };

const _drPlural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const _drLabelOf = (list, key) => (list || []).find((x) => x.key === key)?.label || key;
const _drStatusLabel = (data, key) => _drLabelOf(data && data.statuses, key);
// "Oct 20" — date-only values pinned to local midnight.
const _drDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const _drTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const _drOverdue = (i) => !!i.due_date && !['ok', 'na'].includes(i.status) && i.due_date < window.localISODate();
// The owner wrote last on this thread — they're waiting on us.
const _drOwnerLast = (thread) => !!(thread && thread.length && thread[thread.length - 1].side === 'owner');
// To approve starts with everything ticked except what Atlas already has.
const _drDefaultPick = (i) => i.atlas_lookup !== 'found';

// Team members for the assignee picker — one fetch per page load.
let _drMembersP = null;
const useDrMembers = () => {
  const [members, setMembers] = React.useState([]);
  React.useEffect(() => {
    let live = true;
    if (!_drMembersP) _drMembersP = window.AtlasData.loadTeamMembers().catch(() => []);
    _drMembersP.then((m) => {
      if (!Array.isArray(m) || !m.length) _drMembersP = null;
      if (live) setMembers(Array.isArray(m) ? m : []);
    });
    return () => { live = false; };
  }, []);
  return members;
};

const useDataRoom = (companyId) => {
  const { useState, useEffect, useCallback } = React;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = (t) => window.pushToast && window.pushToast(t);

  const load = useCallback(() => window.AtlasApi.dataRoom(companyId)
    .then((r) => { setData(r); setErr(''); })
    .catch((e) => setErr((e && e.message) || 'Failed to load the data room')), [companyId]);
  useEffect(() => { setData(null); load(); }, [companyId]);

  // Every write returns the whole room; swap it in.
  const run = async (fn, success) => {
    if (busy) return null;
    setBusy(true);
    try {
      const r = await fn();
      if (r && r.items) setData(r);
      if (success) toast({ type: 'success', title: typeof success === 'function' ? success(r) : success });
      return r || true;
    } catch (e) {
      toast({ type: 'error', title: 'Could not save', body: e.message });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { data, err, busy, load, run };
};

// A checkbox that can show "some" (indeterminate) and never opens the row
// it sits on.
const DrCheck = ({ checked, mixed = false, onChange, label }) => {
  const ref = React.useRef(null);
  React.useEffect(() => { if (ref.current) ref.current.indeterminate = !!mixed && !checked; }, [mixed, checked]);
  return (
    <input ref={ref} type="checkbox" checked={!!checked} aria-label={label}
      onChange={(e) => onChange(e.target.checked)} onClick={(e) => e.stopPropagation()}
      style={{ accentColor: 'var(--accent)', width: 15, height: 15, margin: 0, cursor: 'pointer', flexShrink: 0 }} />
  );
};

const DataRoomPane = ({ companyId, v, phone, q = '', initialFilter }) => {
  const { useState, useMemo, useEffect } = React;
  const dr = useDataRoom(companyId);
  const api = window.AtlasApi;
  const [filter, setFilter] = useState(initialFilter || 'all');
  const [selId, setSelId] = useState(null);
  const [panel, setPanel] = useState(null);     // 'import' | 'add' | 'messages' | null
  const [picks, setPicks] = useState({});      // draft id → ticked, over the default
  useEffect(() => { if (initialFilter) setFilter(initialFilter); }, [initialFilter]);
  useEffect(() => { setPicks({}); setSelId(null); setPanel(null); }, [companyId]);
  const data = dr.data;
  const room = data && data.room;
  const items = (data && data.items) || [];
  const folders = (data && data.folders) || [];
  const folderLabel = (k) => folders.find((f) => f.key === k)?.label || k;

  const shown = useMemo(() => {
    const f = DR_FILTERS.find((x) => x.key === filter) || DR_FILTERS[1];
    const needle = q.trim().toLowerCase();
    return items.filter((i) => f.test(i) && (!needle || [i.title, i.what, i.key].some((s) => (s || '').toLowerCase().includes(needle))));
  }, [items, filter, q]);
  const sel = selId ? items.find((i) => i.id === selId) : null;

  if (data === null) {
    return <div style={{ flex: 1, padding: 16 }}>{dr.err ? <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{dr.err}</div> : <window.SkeletonRows rows={6} />}</div>;
  }

  if (!room && panel !== 'import') {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32, textAlign: 'center' }}>
        <window.Icon.Users size={22} color="var(--text-3)" />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>No data room yet</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <window.Btn size="sm" variant="accent" disabled={dr.busy} icon={<window.Icon.Plus size={12} />}
            onClick={() => dr.run(() => api.startDataRoom(companyId), 'Data room started')}>Start data room</window.Btn>
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Upload size={12} />} onClick={() => setPanel('import')}>Import request list</window.Btn>
        </div>
      </div>
    );
  }

  const s = data.summary || {};
  const toReview = items.filter((i) => _drLive(i) && (i.status === 'up' || i.status === 'rev')).length;
  const drafts = items.filter((i) => i.draft);
  const nDrafts = s.drafts != null ? s.drafts : drafts.length;
  const people = (data.people || []).filter((p) => !p.revoked);
  const roomThread = data.thread || [];
  const isPicked = (i) => (i.id in picks ? picks[i.id] : _drDefaultPick(i));
  const setPicked = (list, on) => setPicks((p) => { const x = { ...p }; list.forEach((i) => { x[i.id] = on; }); return x; });
  const forget = (ids) => setPicks((p) => { const x = { ...p }; ids.forEach((id) => { delete x[id]; }); return x; });

  // Owners reached by a message on this item (or the room) right now.
  const reaches = (i) => room && room.status === 'open' && (!i || (!i.draft && !i.internal && !i.dismissed && !i.archived));
  const postMessage = (i) => (text) => dr.run(() => api.postDataRoomMessage(companyId, i ? i.id : null, text),
    reaches(i) ? 'Message sent' : 'Message saved');

  const sendIds = async (ids) => {
    if (!ids.length) return null;
    const open = room.status === 'open';
    if (!items.some((i) => i.sent_at)) {
      const ok = await window.showConfirm({
        title: `Send ${_drPlural(ids.length, 'request')} to the owners?`,
        body: open ? undefined : 'They’ll see them once access is granted.',
        confirmLabel: 'Send',
      });
      if (!ok) return null;
    }
    const r = await dr.run(() => api.sendDataRoomItems(companyId, ids), (res) => {
      const n = _drPlural((res && res.sent) || 0, 'request');
      return open ? `Sent ${n}` : `${n} sent — the owners see them once access is granted`;
    });
    if (r) forget(ids);
    return r;
  };
  const dismissIds = async (ids) => {
    if (!ids.length) return null;
    const r = await dr.run(() => api.dismissDataRoomItems(companyId, ids),
      (res) => `Dismissed ${_drPlural((res && res.dismissed) || 0, 'request')}`);
    if (r) forget(ids);
    return r;
  };
  const restore = (i) => dr.run(() => api.patchDataRoomItem(companyId, i.id, { dismissed: false }), `${i.title}: back in To approve`);

  const grantAccess = async () => {
    const pending = people.filter((p) => !p.invited_at).length;
    const pick = { ids: [] };
    const text = people.length
      ? `The owners can sign in and see every request you've sent.${pending ? ` ${pending} invite${pending === 1 ? '' : 's'} go${pending === 1 ? 'es' : ''} out now${room.advisor_name ? ` from ${room.advisor_name}` : ''}.` : ''}`
      : 'Nobody has access yet — add the owners after opening the room and their invites go out right away.';
    const ok = await window.showConfirm({
      title: 'Grant access?',
      body: drafts.length
        ? <><div>{text}</div><DataRoomGrantDrafts drafts={drafts} onChange={(ids) => { pick.ids = ids; }} /></>
        : text,
      confirmLabel: 'Grant access',
    });
    if (!ok) return;
    const ids = pick.ids.slice();
    const r = await dr.run(() => api.patchDataRoom(companyId, { status: 'open', send_invites: true, ...(ids.length ? { send_item_ids: ids } : {}) }),
      ids.length ? `Access granted · ${_drPlural(ids.length, 'request')} sent` : 'Access granted');
    if (r && ids.length) forget(ids);
  };
  const closeRoom = async () => {
    const ok = await window.showConfirm({
      title: 'Close the data room?', body: 'Owners are signed out and can no longer sign in. Their files stay in the vault.',
      confirmLabel: 'Close room', danger: true,
    });
    if (ok) dr.run(() => api.patchDataRoom(companyId, { status: 'closed' }), 'Data room closed');
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(room.portal_url); window.pushToast && window.pushToast({ type: 'success', title: 'Portal link copied' }); }
    catch (_) { window.pushToast && window.pushToast({ type: 'info', title: room.portal_url }); }
  };
  const openItem = (id) => { setSelId(id); setPanel(null); };

  const top = room && (
    <div style={{ flexShrink: 0, borderBottom: '1px solid var(--border)', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <window.Pill hue={room.status === 'open' ? 'green' : room.status === 'closed' ? 'slate' : 'amber'} dot>{DR_ROOM_STATUS[room.status]}</window.Pill>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{s.accepted || 0} of {s.total || 0} accepted</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
          Essential {s.essential_accepted || 0}/{s.essential_total || 0}
          {toReview ? ` · ${toReview} to review` : ''}
          {nDrafts ? ` · ${nDrafts} to approve` : ''}
          {room.advisor_name ? ` · ${room.advisor_name}` : ''}
        </span>
        <div style={{ flex: 1 }} />
        <window.Btn size="sm" variant="ghost" icon={<window.Icon.Upload size={12} />} onClick={() => setPanel(panel === 'import' ? null : 'import')}>Import list</window.Btn>
        <window.Btn size="sm" variant="ghost" icon={<window.Icon.Plus size={12} />} onClick={() => setPanel(panel === 'add' ? null : 'add')}>Add item</window.Btn>
        <window.Btn size="sm" variant="ghost" icon={<window.Icon.Copy size={12} />} onClick={copyLink}>Portal link</window.Btn>
        {room.status === 'open'
          ? <window.Btn size="sm" variant="outline" disabled={dr.busy} onClick={closeRoom}>Close room</window.Btn>
          : <window.Btn size="sm" variant="accent" disabled={dr.busy} icon={<window.Icon.Check size={12} />} onClick={grantAccess}>Grant access</window.Btn>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DataRoomPeople dr={dr} companyId={companyId} room={room} people={data.people || []} />
        </div>
        <window.Btn size="sm" variant="ghost" active={panel === 'messages'} icon={<window.Icon.Mail size={12} />}
          onClick={() => setPanel(panel === 'messages' ? null : 'messages')}>
          Messages
          {roomThread.length > 0 && <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{roomThread.length}</span>}
          {_drOwnerLast(roomThread) && <span title="The owner wrote last" style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--accent)' }} />}
        </window.Btn>
      </div>
    </div>
  );

  const chips = (
    <div style={{ display: 'flex', gap: 6, padding: '8px 14px', borderBottom: '1px solid var(--border)', overflowX: 'auto', flexShrink: 0 }}>
      {DR_FILTERS.map((f) => {
        const n = f.key === 'approve' ? nDrafts : items.filter(f.test).length;
        const on = filter === f.key;
        if ((f.key === 'archived' || f.key === 'dismissed' || f.key === 'approve') && !n && !on) return null;
        return (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)}
            style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
              border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'var(--accent-soft)' : 'var(--surface)',
              color: on ? 'var(--accent-text)' : 'var(--text-2)' }}>
            {f.label} <span style={{ color: f.key === 'approve' && n ? 'var(--accent-text)' : 'var(--text-4)', fontWeight: f.key === 'approve' && n ? 600 : 400, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
          </button>
        );
      })}
    </div>
  );

  const groupHead = (key, icon, label, count, check) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 6px' }}>
      {check || icon}
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontSize: 11, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
    </div>
  );

  const empty = shown.length === 0 && (
    <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)' }}>
      {q.trim() && items.length ? 'No items match.'
        : filter === 'approve' ? 'No drafts'
          : filter === 'dismissed' ? 'Nothing dismissed'
            : items.length ? 'No items match.' : 'No items yet — import the request list or add items.'}
    </div>
  );

  // To approve: drafts by category (Immediate / Management interview /
  // Follow-up, then everything else), ticked to send or dismiss.
  const approveList = () => {
    const cats = data.categories || [];
    const known = new Set(cats.map((c) => c.key));
    const groups = [
      ...cats.map((c) => ({ key: c.key, label: c.label, its: shown.filter((i) => i.category === c.key) })),
      { key: '_other', label: 'Other', its: shown.filter((i) => !known.has(i.category)) },
    ].filter((g) => g.its.length);
    const pickedIds = shown.filter(isPicked).map((i) => i.id);
    const n = pickedIds.length;
    return (
      <>
        {shown.length > 0 && (
          <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-3)', cursor: 'pointer', fontVariantNumeric: 'tabular-nums' }}>
              <DrCheck checked={n === shown.length} mixed={n > 0} onChange={(on) => setPicked(shown, on)} label="Select every draft" />
              {n} of {shown.length} selected
            </label>
            <div style={{ flex: 1 }} />
            <window.Btn size="sm" variant="outline" disabled={dr.busy || !n} onClick={() => dismissIds(pickedIds)}>Dismiss {n}</window.Btn>
            <window.Btn size="sm" variant="accent" disabled={dr.busy || !n} icon={<window.Icon.Mail size={12} />} onClick={() => sendIds(pickedIds)}>Send {n}</window.Btn>
          </div>
        )}
        {groups.map((g) => {
          const on = g.its.filter(isPicked).length;
          return (
            <div key={g.key}>
              {groupHead(g.key, null, g.label, `${on}/${g.its.length}`,
                <DrCheck checked={on === g.its.length} mixed={on > 0} onChange={(x) => setPicked(g.its, x)} label={`Select every ${g.label} draft`} />)}
              {g.its.map((i) => (
                <DataRoomDraftRow key={i.id} item={i} on={selId === i.id} data={data} folderLabel={folderLabel}
                  picked={isPicked(i)} onPick={(x) => setPicked([i], x)} onClick={() => openItem(i.id)} />
              ))}
            </div>
          );
        })}
      </>
    );
  };

  const folderList = () => folders.map((f) => {
    const its = shown.filter((i) => i.folder === f.key);
    if (!its.length) return null;
    return (
      <div key={f.key}>
        {groupHead(f.key, <window.Icon.Folder size={13} color="var(--text-4)" />, f.label, `${its.filter((i) => i.status === 'ok').length}/${its.length}`)}
        {its.map((i) => (
          <DataRoomRow key={i.id} item={i} on={selId === i.id} data={data} onClick={() => openItem(i.id)}
            action={i.dismissed && (
              <window.Btn size="sm" variant="ghost" disabled={dr.busy} onClick={(e) => { e.stopPropagation(); restore(i); }}>Restore</window.Btn>
            )} />
        ))}
      </div>
    );
  });

  const list = (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {empty}
      {filter === 'approve' ? approveList() : folderList()}
    </div>
  );

  const side = panel === 'import'
    ? <DataRoomImport dr={dr} companyId={companyId} replaceable={items.length > 0} onDone={() => setPanel(null)} />
    : panel === 'add'
      ? <DataRoomAddItem dr={dr} companyId={companyId} folders={folders}
          onDone={(r, asDraft) => {
            setPanel(null);
            if (!r || !r.items || !r.items.length) return;
            setSelId(r.items[r.items.length - 1].id);
            if (asDraft) setFilter('approve');
            else if (filter === 'approve' || filter === 'dismissed') setFilter('all');
          }} />
      : panel === 'messages'
        ? <>
            {_drPaneHead('Messages', () => setPanel(null))}
            <DataRoomThread grow thread={roomThread} canPost busy={dr.busy} onPost={postMessage(null)} placeholder="Message the owners…" />
          </>
        : sel
          ? <DataRoomItemPane key={sel.id} item={sel} data={data} dr={dr} v={v} companyId={companyId} phone={phone}
              folderLabel={folderLabel} onClose={() => setSelId(null)}
              onSend={() => sendIds([sel.id])} onDismiss={() => dismissIds([sel.id])} onRestore={() => restore(sel)}
              onPost={postMessage(sel)} />
          : null;

  if (phone && side) return <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{side}</div>;
  return (
    <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {top}
        {chips}
        {list}
      </div>
      {!phone && side && (
        <aside style={{ width: 420, flexShrink: 0, borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)' }}>
          {side}
        </aside>
      )}
    </div>
  );
};

// Priority · kind · files · due · assignee — one muted line under a title.
const DrMeta = ({ item: i, extra = [], files = true }) => {
  const bits = [
    DR_PRIORITY[i.priority],
    ...extra,
    i.kind !== 'file' ? DR_KIND[i.kind] || i.kind : null,
    i.internal ? 'Internal' : null,
    files && i.files.length ? `${i.files.length} file${i.files.length === 1 ? '' : 's'}` : null,
    i.due_date ? <span style={{ color: _drOverdue(i) ? 'var(--rose-fg)' : undefined }}>Due {_drDay(i.due_date)}</span> : null,
    i.assignee_name || null,
  ].filter(Boolean);
  return (
    <div style={{ fontSize: 11.5, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {bits.map((b, k) => <React.Fragment key={k}>{k ? ' · ' : ''}{b}</React.Fragment>)}
    </div>
  );
};

const DrUnreadDot = () => (
  <span title="The owner wrote last" aria-label="The owner wrote last"
    style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--accent)', flexShrink: 0 }} />
);

const DataRoomRow = ({ item: i, on, data, onClick, action }) => {
  const done = i.status === 'ok';
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderTop: '1px solid var(--border)',
        background: on ? 'var(--accent-soft)' : 'transparent', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none', opacity: i.archived || i.dismissed ? 0.6 : 1 }}>
      <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `2px ${i.status === 'up' || i.status === 'prog' ? 'dashed' : 'solid'} ${done ? 'var(--green-fg)' : i.status === 'fix' ? 'var(--rose-fg)' : i.status === 'rev' ? 'var(--amber-fg)' : 'var(--border)'}`,
        background: done ? 'var(--green-fg)' : 'transparent' }}>
        {done && <window.Icon.Check size={10} color="var(--surface)" />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ minWidth: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.title}</span>
          {_drOwnerLast(i.thread) && <DrUnreadDot />}
        </div>
        <DrMeta item={i} />
      </div>
      {action}
      <window.Pill hue={i.dismissed ? 'slate' : DR_STATUS_HUE[i.status]} sz="sm">{i.dismissed ? 'Dismissed' : _drStatusLabel(data, i.status)}</window.Pill>
    </div>
  );
};

const DataRoomDraftRow = ({ item: i, on, data, folderLabel, picked, onPick, onClick }) => (
  <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 14px', cursor: 'pointer', borderTop: '1px solid var(--border)',
      background: on ? 'var(--accent-soft)' : 'transparent', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none' }}>
    <label onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', padding: 6, margin: '-5px -6px -6px', cursor: 'pointer', flexShrink: 0 }}>
      <DrCheck checked={picked} onChange={onPick} label={`Select ${i.title}`} />
    </label>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.title}</span>
        {_drOwnerLast(i.thread) && <DrUnreadDot />}
        {i.atlas_lookup && <window.Pill hue={DR_LOOKUP_HUE[i.atlas_lookup] || 'slate'} sz="sm">{_drLabelOf(data.lookups, i.atlas_lookup)}</window.Pill>}
      </div>
      <DrMeta item={i} extra={[folderLabel(i.folder)]} files={false} />
      {i.lookup_detail && (
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{i.lookup_detail}</div>
      )}
      {i.files.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
          {i.files.map((f) => (
            <button key={f.id} type="button" title={f.name} onClick={(e) => { e.stopPropagation(); window.AtlasApi.openAttachment(f.id, f.name); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: 220, padding: '1px 7px', borderRadius: 999, border: '1px solid var(--border)',
                background: 'var(--surface)', color: 'var(--accent-text)', fontSize: 11, cursor: 'pointer' }}>
              <window.Icon.Doc size={10} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);

// Grant access: the drafts that may go out with it — none ticked to start.
const DataRoomGrantDrafts = ({ drafts, onChange }) => {
  const [on, setOn] = React.useState({});
  const ids = drafts.filter((d) => on[d.id]).map((d) => d.id);
  React.useEffect(() => { onChange(ids); }, [ids.join(',')]);
  return (
    <div style={{ marginTop: 12 }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, color: 'var(--text)', marginBottom: 6, cursor: 'pointer' }}>
        <DrCheck checked={ids.length === drafts.length} mixed={ids.length > 0} label="Send every draft"
          onChange={(x) => setOn(x ? Object.fromEntries(drafts.map((d) => [d.id, true])) : {})} />
        <span style={{ flex: 1 }}>Send drafts too</span>
        <span style={{ fontWeight: 400, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{ids.length}/{drafts.length}</span>
      </label>
      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
        {drafts.map((d, k) => (
          <label key={d.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', borderTop: k ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
            <DrCheck checked={!!on[d.id]} onChange={(x) => setOn({ ...on, [d.id]: x })} label={d.title} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{d.title}</span>
            {d.atlas_lookup === 'found' && <window.Pill hue="green" sz="sm">In Atlas</window.Pill>}
          </label>
        ))}
      </div>
    </div>
  );
};

const DataRoomPeople = ({ dr, companyId, room, people }) => {
  const { useState } = React;
  const api = window.AtlasApi;
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const live = people.filter((p) => !p.revoked);
  const add = async () => {
    const r = await dr.run(() => api.addDataRoomPerson(companyId, { email, name, send_invite: true }),
      room.status === 'open' ? `Invite sent to ${email.trim()}` : `${email.trim()} added — invited when you grant access`);
    if (r) { setEmail(''); setName(''); setAdding(false); }
  };
  const revoke = async (p) => {
    const ok = await window.showConfirm({ title: `Remove ${p.name || p.email}?`, body: 'They are signed out and can no longer sign in.', confirmLabel: 'Remove access', danger: true });
    if (ok) dr.run(() => api.revokeDataRoomPerson(companyId, p.id), 'Access removed');
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <window.Icon.Users size={13} color="var(--text-4)" />
      {live.length === 0 && !adding && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>Nobody has access</span>}
      {live.map((p) => (
        <span key={p.id} title={p.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 4px 3px 9px', borderRadius: 999, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-2)', background: 'var(--surface)' }}>
          <span style={{ fontWeight: 500, color: 'var(--text)' }}>{p.name || p.email}</span>
          <span style={{ color: 'var(--text-4)' }}>
            {p.last_seen_at ? `seen ${window.fmtDate(p.last_seen_at)}` : p.invited_at ? `invited ${window.fmtDate(p.invited_at)}` : 'not invited'}
          </span>
          {room.status === 'open' && (
            <button type="button" title="Send the invite again" onClick={() => dr.run(() => api.inviteDataRoomPerson(companyId, p.id), `Invite sent to ${p.email}`)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}><window.Icon.Mail size={12} /></button>
          )}
          <button type="button" title="Remove access" onClick={() => revoke(p)}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', padding: '0 4px', fontSize: 14, lineHeight: 1 }}>×</button>
        </span>
      ))}
      {adding ? (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@company.com" aria-label="Email"
            onKeyDown={(e) => { if (e.key === 'Enter' && email.includes('@')) add(); }} style={{ ..._drInput, width: 200 }} />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Name" style={{ ..._drInput, width: 140 }} />
          <window.Btn size="sm" variant="accent" disabled={dr.busy || !email.includes('@')} onClick={add}>Add</window.Btn>
          <window.Btn size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</window.Btn>
        </span>
      ) : (
        <window.Btn size="sm" variant="ghost" icon={<window.Icon.Plus size={12} />} onClick={() => setAdding(true)}>Add person</window.Btn>
      )}
    </div>
  );
};

// A thread with the owners — theirs on the left, ours on the right. `grow`
// = it fills a pane (the room's thread) with the box pinned to the bottom.
const DataRoomThread = ({ thread, canPost, busy, onPost, placeholder = 'Reply…', grow = false }) => {
  const { useState, useRef, useEffect } = React;
  const [text, setText] = useState('');
  const scrollRef = useRef(null);
  useEffect(() => {
    if (grow && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.length]);
  const post = async () => {
    const body = text.trim();
    if (!body || busy) return;
    if (await onPost(body)) setText('');
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...(grow ? { flex: 1, minHeight: 0, gap: 0 } : null) }}>
      <div ref={scrollRef} style={{ display: 'flex', flexDirection: 'column', gap: 6,
        ...(grow ? { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '12px 16px' } : null) }}>
        {thread.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No messages</div>}
        {thread.map((m) => {
          const owner = m.side === 'owner';
          return (
            <div key={m.id} style={{ alignSelf: owner ? 'flex-start' : 'flex-end', maxWidth: '88%', borderRadius: 8, padding: '7px 10px',
              background: owner ? 'var(--surface-2)' : 'var(--accent-soft)', border: `1px solid ${owner ? 'var(--border)' : 'transparent'}` }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 11.5 }}>
                <span style={{ fontWeight: 600, color: owner ? 'var(--text)' : 'var(--accent-text)' }}>{m.author}</span>
                <span style={{ color: 'var(--text-4)' }}>{_drTime(m.created_at)}</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.45, marginTop: 2 }}>{m.body}</div>
            </div>
          );
        })}
      </div>
      {canPost && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end',
          ...(grow ? { padding: '10px 16px', borderTop: '1px solid var(--border)', flexShrink: 0 } : null) }}>
          <textarea value={text} rows={2} placeholder={placeholder} aria-label="Message" onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); } }}
            style={{ ..._drInput, flex: 1, resize: 'vertical' }} />
          <window.Btn size="sm" variant="accent" disabled={busy || !text.trim()} onClick={post}>Send</window.Btn>
        </div>
      )}
    </div>
  );
};

const _drPaneHead = (title, onClose) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
    <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{title}</div>
    <button type="button" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 20, cursor: 'pointer' }}>×</button>
  </div>
);

const DataRoomItemPane = ({ item: i, data, dr, v, companyId, phone, folderLabel, onClose, onSend, onDismiss, onRestore, onPost }) => {
  const { useState, useEffect } = React;
  const api = window.AtlasApi;
  const members = useDrMembers();
  const [fixing, setFixing] = useState(false);
  const [note, setNote] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [markReady, setMarkReady] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: i.title, what: i.what || '', why: i.why || '', formats: i.formats || '',
    folder: i.folder, priority: i.priority, kind: i.kind || 'file',
  });
  const [due, setDue] = useState(i.due_date || '');
  useEffect(() => { setDue(i.due_date || ''); }, [i.due_date]);
  const thread = i.thread || [];
  const linked = new Set(i.files.map((f) => f.id));
  // a CIM file (filed_via 'cim') never covers a request: the server refuses it (data_room.cim_file_ids)
  const linkable = ((v && v.files) || []).filter((f) => !linked.has(f.id) && f.filed_via !== 'cim')
    .sort((a, b) => (a.folder === i.folder ? 0 : 1) - (b.folder === i.folder ? 0 : 1) || (a.original_filename || '').localeCompare(b.original_filename || ''));
  const vaultFiles = i.files.filter((f) => f.in_vault).length;

  const setStatus = (status, n) => dr.run(() => api.setDataRoomStatus(companyId, i.id, status, n), `${i.title}: ${_drStatusLabel(data, status)}`);
  const accept = async () => {
    if (vaultFiles && !accepting) { setAccepting(true); return; }
    const ready = accepting && markReady;
    const r = await dr.run(() => api.setDataRoomStatus(companyId, i.id, 'ok', null, ready),
      `${i.title}: ${_drStatusLabel(data, 'ok')}${ready ? ' · Ready for CIM' : ''}`);
    if (r) {
      setAccepting(false);
      setMarkReady(true);
      if (ready && v && v.load) v.load();
    }
  };
  const patch = (body, msg) => dr.run(() => api.patchDataRoomItem(companyId, i.id, body), msg);
  const saveDue = (val) => {
    if ((val || null) === (i.due_date || null)) return;
    patch({ due_date: val || null }, val ? `Due ${_drDay(val)}` : 'Due date cleared');
  };
  const memberOpts = [
    { value: '', label: 'Unassigned' },
    ...members.filter((m) => m.active !== false || m.id === i.assignee_member_id).map((m) => ({ value: m.id, label: m.full_name || m.email })),
  ];
  if (i.assignee_member_id && !memberOpts.some((o) => o.value === i.assignee_member_id)) {
    memberOpts.push({ value: i.assignee_member_id, label: i.assignee_name || 'Assigned' });
  }

  const section = (label, body) => body ? (
    <div><div style={_drLabel}>{label}</div><div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{body}</div></div>
  ) : null;
  const box = (hueFg, hueBg, label, body) => (
    <div style={{ borderLeft: `3px solid ${hueFg}`, background: hueBg, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ ..._drLabel, color: hueFg }}>{label}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{body}</div>
    </div>
  );
  const select = (k, opts) => (
    <window.Picker value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })} width="100%"
      options={opts.map(([val, label]) => ({ value: val, label }))} />
  );

  return (
    <>
      {_drPaneHead(i.title, onClose)}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {i.dismissed
            ? <window.Pill hue="slate" dot>Dismissed</window.Pill>
            : i.draft
              ? <window.Pill hue="amber" dot>Draft</window.Pill>
              : null}
          {(!i.draft && !i.dismissed) || i.status !== 'none'
            ? <window.Pill hue={DR_STATUS_HUE[i.status]} dot={!i.draft && !i.dismissed} sz={i.draft || i.dismissed ? 'sm' : 'md'}>{_drStatusLabel(data, i.status)}</window.Pill>
            : null}
          <window.Pill hue={i.priority === 'P0' ? 'violet' : 'slate'} sz="sm">{DR_PRIORITY[i.priority]}</window.Pill>
          <window.Pill sz="sm">{folderLabel(i.folder)}</window.Pill>
          {i.kind !== 'file' && <window.Pill hue="blue" sz="sm">{DR_KIND[i.kind] || i.kind}</window.Pill>}
          {i.internal && <window.Pill hue="amber" sz="sm">Internal</window.Pill>}
          {i.category && <window.Pill sz="sm">{_drLabelOf(data.categories, i.category)}</window.Pill>}
          {i.status_at && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{window.fmtDate(i.status_at)}</span>}
        </div>

        {i.atlas_lookup && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div><window.Pill hue={DR_LOOKUP_HUE[i.atlas_lookup] || 'slate'} sz="sm">{_drLabelOf(data.lookups, i.atlas_lookup)}</window.Pill></div>
            {i.lookup_detail && <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{i.lookup_detail}</div>}
          </div>
        )}

        {i.status === 'fix' && i.fix_note && box('var(--rose-fg)', 'var(--rose-bg)', 'Asked the owner', i.fix_note)}
        {i.review_note && (i.status === 'rev' || i.status === 'up') && box('var(--amber-fg)', 'var(--amber-bg)', 'Why it may already be covered', i.review_note)}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 140px', minWidth: 0 }}>
            <div style={_drLabel}>Due</div>
            <input type="date" value={due} aria-label="Due date"
              onChange={(e) => { const val = e.target.value; setDue(val); if (!val || /^20\d\d-\d\d-\d\d$/.test(val)) saveDue(val); }}
              onBlur={() => { if (!due || /^20\d\d-\d\d-\d\d$/.test(due)) saveDue(due); }}
              style={{ ..._drInput, color: _drOverdue(i) ? 'var(--rose-fg)' : 'var(--text)' }} />
          </label>
          <div style={{ flex: '2 1 180px', minWidth: 0 }}>
            <div style={_drLabel}>Assignee</div>
            <window.Combobox searchable value={i.assignee_member_id || ''} options={memberOpts} placeholder="Unassigned" disabled={dr.busy}
              onChange={(e) => {
                const id = e.target.value || null;
                if (id === (i.assignee_member_id || null)) return;
                patch({ assignee_member_id: id }, id ? `Assigned to ${(memberOpts.find((o) => o.value === id) || {}).label || 'them'}` : 'Unassigned');
              }} style={{ width: '100%' }} />
          </div>
        </div>

        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['title', 'Title'], ['what', 'What to send'], ['why', 'Why it matters'], ['formats', 'Accepted formats']].map(([k, label]) => (
              <label key={k} style={{ display: 'block' }}>
                <div style={_drLabel}>{label}</div>
                {k === 'title'
                  ? <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={_drInput} />
                  : <textarea value={form[k]} rows={k === 'formats' ? 1 : 3} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={{ ..._drInput, resize: 'vertical' }} />}
              </label>
            ))}
            <div style={{ display: 'flex', gap: 8, flexWrap: phone ? 'wrap' : 'nowrap' }}>
              <label style={{ flex: '2 1 140px', minWidth: 0 }}><div style={_drLabel}>Folder</div>
                {select('folder', (data.folders || []).map((o) => [o.key, o.label]))}
              </label>
              <label style={{ flex: '1 1 100px', minWidth: 0 }}><div style={_drLabel}>Priority</div>{select('priority', Object.entries(DR_PRIORITY))}</label>
              <label style={{ flex: '1 1 100px', minWidth: 0 }}><div style={_drLabel}>Kind</div>{select('kind', Object.entries(DR_KIND))}</label>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <window.Btn size="sm" variant="accent" disabled={dr.busy || !form.title.trim()}
                onClick={async () => { if (await patch(form, 'Saved')) setEditing(false); }}>Save</window.Btn>
              <window.Btn size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</window.Btn>
            </div>
          </div>
        ) : (
          <>
            {section('What to send', i.what)}
            {section('Why it matters', i.why)}
            {section('Accepted formats', i.formats)}
          </>
        )}

        <div>
          <div style={_drLabel}>Files · {i.files.length}</div>
          {i.files.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Nothing yet.</div>}
          {i.files.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--border)' }}>
              <button type="button" onClick={() => api.openAttachment(f.id, f.name)} title="Open"
                style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <div style={{ fontSize: 12.5, color: 'var(--accent-text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
                  {[f.filed_via === 'portal' ? `sent by ${f.sent_by || 'the owner'}` : f.filed_via === 'email' ? 'from email' : 'from the vault',
                    f.received_at ? window.fmtDate(f.received_at) : null, f.in_vault ? null : 'removed from the vault'].filter(Boolean).join(' · ')}
                </div>
              </button>
              <button type="button" title="Take off this item" onClick={() => dr.run(() => api.unlinkDataRoomFile(companyId, i.id, f.id))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', fontSize: 16, padding: '0 4px' }}>×</button>
            </div>
          ))}
          {linkable.length > 0 && (
            <window.Picker value="" placeholder="Link a vault file…" width="100%" style={{ marginTop: 8 }}
              onChange={(id) => { if (id) dr.run(() => api.linkDataRoomFile(companyId, i.id, id), 'File linked'); }}
              options={linkable.slice(0, 300).map((f) => ({ value: f.id, label: f.original_filename, hint: v.labelOf(f.folder) }))} />
          )}
        </div>

        {i.dismissed ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <window.Btn size="sm" variant="outline" disabled={dr.busy} onClick={onRestore}>Restore</window.Btn>
          </div>
        ) : i.draft ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <window.Btn size="sm" variant="accent" disabled={dr.busy} icon={<window.Icon.Mail size={12} />} onClick={onSend}>Send to owners</window.Btn>
            <window.Btn size="sm" variant="outline" disabled={dr.busy} onClick={onDismiss}>Dismiss</window.Btn>
          </div>
        ) : fixing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={_drLabel}>What needs to change</div>
            <textarea autoFocus value={note} rows={3} onChange={(e) => setNote(e.target.value)} style={{ ..._drInput, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <window.Btn size="sm" variant="accent" disabled={dr.busy || !note.trim()}
                onClick={async () => { if (await setStatus('fix', note.trim())) { setFixing(false); setNote(''); } }}>Send to owner</window.Btn>
              <window.Btn size="sm" variant="ghost" onClick={() => setFixing(false)}>Cancel</window.Btn>
            </div>
          </div>
        ) : accepting ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--text-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={markReady} onChange={(e) => setMarkReady(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
              Also mark its {vaultFiles === 1 ? 'file' : `${vaultFiles} files`} Ready for CIM
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <window.Btn size="sm" variant="accent" disabled={dr.busy} icon={<window.Icon.Check size={12} />} onClick={accept}>Accept</window.Btn>
              <window.Btn size="sm" variant="ghost" onClick={() => { setAccepting(false); setMarkReady(true); }}>Cancel</window.Btn>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {i.status !== 'ok' && <window.Btn size="sm" variant="accent" disabled={dr.busy} icon={<window.Icon.Check size={12} />} onClick={accept}>Accept</window.Btn>}
            {!i.internal && i.status !== 'fix' && <window.Btn size="sm" variant="outline" disabled={dr.busy} onClick={() => setFixing(true)}>Request changes</window.Btn>}
            {i.status !== 'na' && <window.Btn size="sm" variant="ghost" disabled={dr.busy} onClick={() => setStatus('na')}>Not applicable</window.Btn>}
            {i.status !== 'none' && <window.Btn size="sm" variant="ghost" disabled={dr.busy} onClick={() => setStatus('none')}>Reset</window.Btn>}
          </div>
        )}

        {(thread.length > 0 || (!i.internal && !i.dismissed && !i.archived)) && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={_drLabel}>Messages · {thread.length}</div>
            <DataRoomThread thread={thread} canPost={!i.internal && !i.dismissed && !i.archived} busy={dr.busy} onPost={onPost} />
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {section('CIM sections', i.cim_sections)}
          {section('Questions', i.question_refs)}
          {section('Likely source', i.likely_source)}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!editing && <window.Btn size="sm" variant="ghost" icon={<window.Icon.Edit size={12} />} onClick={() => setEditing(true)}>Edit</window.Btn>}
            {!i.dismissed && (
              <window.Btn size="sm" variant="ghost" disabled={dr.busy}
                onClick={() => patch({ internal: !i.internal },
                  !i.internal ? 'Hidden from owners' : i.sent_at ? 'Owners see this item' : 'Back in To approve')}>
                {i.internal ? 'Show to owners' : 'Hide from owners'}
              </window.Btn>
            )}
            <window.Btn size="sm" variant="ghost" disabled={dr.busy}
              onClick={() => patch({ archived: !i.archived }, i.archived ? 'Restored' : 'Archived')}>
              {i.archived ? 'Restore' : 'Archive'}
            </window.Btn>
          </div>
        </div>
      </div>
    </>
  );
};

const DataRoomImport = ({ dr, companyId, replaceable, onDone }) => {
  const { useState, useRef } = React;
  const [text, setText] = useState('');
  const [replace, setReplace] = useState(false);
  const [parseErr, setParseErr] = useState('');
  const fileRef = useRef(null);
  const save = async () => {
    let body;
    try {
      const parsed = JSON.parse(text);
      body = Array.isArray(parsed) ? { items: parsed } : { items: parsed.items, framework: parsed.framework };
      if (!Array.isArray(body.items)) throw new Error('Expected a list of items, or {"items": [...]}');
    } catch (e) {
      setParseErr(e.message);
      return;
    }
    setParseErr('');
    const r = await dr.run(() => window.AtlasApi.importDataRoom(companyId, { ...body, replace }),
      (res) => { const x = (res && res.result) || {}; return `Saved: ${x.created || 0} new, ${x.updated || 0} updated${x.archived ? `, ${x.archived} archived` : ''}`; });
    if (r) onDone();
  };
  return (
    <>
      {_drPaneHead('Import request list', onDone)}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={14} spellCheck={false}
          placeholder={'{"items": [{"key": "annual-financials", "title": "…", "folder": "Financials", "priority": "P0"}]}'}
          style={{ ..._drInput, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, resize: 'vertical' }} />
        <input ref={fileRef} type="file" accept=".json,application/json" style={{ display: 'none' }}
          onChange={async (e) => { const f = e.target.files && e.target.files[0]; if (f) setText(await f.text()); if (fileRef.current) fileRef.current.value = ''; }} />
        {parseErr && <div style={{ fontSize: 12, color: 'var(--rose-fg)' }}>{parseErr}</div>}
        {replaceable && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--text-2)' }}>
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Archive items not in this list
          </label>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <window.Btn size="sm" variant="accent" disabled={dr.busy || !text.trim()} onClick={save}>Save list</window.Btn>
          <window.Btn size="sm" variant="outline" icon={<window.Icon.Upload size={12} />} onClick={() => fileRef.current && fileRef.current.click()}>Choose .json</window.Btn>
        </div>
      </div>
    </>
  );
};

const DataRoomAddItem = ({ dr, companyId, folders, onDone }) => {
  const { useState } = React;
  const [f, setF] = useState({ title: '', folder: (folders[0] || {}).key || 'financials', priority: 'P1', kind: 'file', what: '', why: '', formats: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Added items go to the owners at once; a draft waits in To approve.
  const save = async (asDraft) => {
    const r = await dr.run(() => window.AtlasApi.addDataRoomItem(companyId, asDraft ? { ...f, draft: true } : f),
      asDraft ? 'Saved as draft' : 'Item added');
    if (r) onDone(r, asDraft);
  };
  return (
    <>
      {_drPaneHead('Add item', () => onDone(null))}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label><div style={_drLabel}>Title</div><input autoFocus value={f.title} onChange={set('title')} style={_drInput} /></label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <label style={{ flex: '2 1 140px', minWidth: 0 }}><div style={_drLabel}>Folder</div>
            <window.Picker value={f.folder} onChange={(v) => setF({ ...f, folder: v })} width="100%" options={folders.map((o) => ({ value: o.key, label: o.label }))} />
          </label>
          <label style={{ flex: '1 1 100px', minWidth: 0 }}><div style={_drLabel}>Priority</div>
            <window.Picker value={f.priority} onChange={(v) => setF({ ...f, priority: v })} width="100%" options={Object.entries(DR_PRIORITY).map(([k, l]) => ({ value: k, label: l }))} />
          </label>
          <label style={{ flex: '1 1 100px', minWidth: 0 }}><div style={_drLabel}>Kind</div>
            <window.Picker value={f.kind} onChange={(v) => setF({ ...f, kind: v })} width="100%" options={Object.entries(DR_KIND).map(([k, l]) => ({ value: k, label: l }))} />
          </label>
        </div>
        <label><div style={_drLabel}>{f.kind === 'question' ? 'Question' : 'What to send'}</div><textarea value={f.what} rows={3} onChange={set('what')} style={{ ..._drInput, resize: 'vertical' }} /></label>
        <label><div style={_drLabel}>Why it matters</div><textarea value={f.why} rows={2} onChange={set('why')} style={{ ..._drInput, resize: 'vertical' }} /></label>
        {f.kind !== 'question' && <label><div style={_drLabel}>Accepted formats</div><input value={f.formats} onChange={set('formats')} style={_drInput} /></label>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <window.Btn size="sm" variant="accent" disabled={dr.busy || !f.title.trim()} onClick={() => save(false)}>Add item</window.Btn>
          <window.Btn size="sm" variant="outline" disabled={dr.busy || !f.title.trim()} onClick={() => save(true)}>Save as draft</window.Btn>
        </div>
      </div>
    </>
  );
};

window.DataRoomPane = DataRoomPane;
